import type {
  AccountInvitationResponse,
  CreateInvitation,
  InvitationResponse,
} from "@home-finance-manager/shared"
import { BaseSDK } from "./base-sdk"

export class InvitationsSDK extends BaseSDK {
  /**
   * Create invitation(s) for one or multiple accounts
   * @param data - Invitation data (email, accountIds, role)
   * @returns Created invitations
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async createInvitation(
    data: CreateInvitation
  ): Promise<{ message: string; data: AccountInvitationResponse[] }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/invitations`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    })
  }

  /**
   * Get all invitations for user's owned accounts
   * @param status - Optional status filter (pending, accepted, expired, cancelled)
   * @returns List of invitations
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getInvitations(status?: string): Promise<InvitationResponse> {
    const url = new URL(`${this.baseUrl}/invitations`)
    if (status) {
      url.searchParams.set("status", status)
    }

    return this.fetchWithErrorHandling<InvitationResponse>(url.toString(), {
      method: "GET",
      headers: this.getAuthHeaders(),
    })
  }

  /**
   * Cancel an invitation
   * @param invitationId - The invitation ID
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async cancelInvitation(invitationId: number): Promise<{ message: string }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/invitations/${invitationId}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    })
  }

  /**
   * Resend an invitation email
   * @param invitationId - The invitation ID
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async resendInvitation(invitationId: number): Promise<{ message: string }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/invitations/${invitationId}/resend`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    })
  }

  /**
   * Accept an invitation (for existing users)
   * @param token - The invitation token
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async acceptInvitation(token: string): Promise<{ message: string }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/invitations/${token}/accept`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    })
  }
}
